import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Radar } from "lucide-react";
import SellerIdentity from "@/components/SellerIdentity";


const LAST_UPDATED = "5 September 2026";

const Privacy = () => (
  <>
    <Helmet>
      <title>Privacy Policy — Trends Discovery</title>
      <meta
        name="description"
        content="What data Trends Discovery collects, why we collect it, how long we keep it, and how to request deletion of your account data."
      />
      <link rel="canonical" href="https://trendsdiscovery.com/privacy" />
      <meta property="og:title" content="Privacy Policy — Trends Discovery" />
      <meta
        property="og:description"
        content="What data Trends Discovery collects, why, and how to request deletion."
      />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://trendsdiscovery.com/privacy" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>

    <main className="min-h-screen bg-background text-foreground">
      <div className="container max-w-3xl py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-xs text-primary hover:underline"
        >
          <ArrowLeft className="w-3 h-3" /> BACK TO HOME
        </Link>

        <div className="mt-8 flex items-center gap-2">
          <span className="grid place-items-center w-7 h-7 rounded bg-primary text-primary-foreground">
            <Radar className="w-4 h-4" />
          </span>
          <span className="font-display font-semibold">Trends.Discovery</span>
        </div>

        <h1 className="mt-6 font-display text-4xl md:text-5xl font-bold">Privacy Policy</h1>
        <p className="mt-3 font-mono text-xs text-muted-foreground">
          LAST UPDATED: {LAST_UPDATED}
        </p>

        <div className="mt-10 space-y-10 text-sm leading-relaxed text-muted-foreground">
          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              1. The short version
            </h2>
            <p>
              We collect the minimum needed to run your account and improve the product: your email,
              your license status, your in-app preferences and basic usage events. We do not sell
              your personal data, and we do not share it with advertisers.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              2. What we collect
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Account data</strong> — your email address and authentication details, so you
                can sign in and we can tie your license to you.
              </li>
              <li>
                <strong>Purchase data</strong> — the fact and status of your $39 license, plus the
                transaction reference from our payment provider. Card numbers never reach our
                servers.
              </li>
              <li>
                <strong>Product data</strong> — saved and shared blueprints, notification
                preferences, and which trends you have already seen.
              </li>
              <li>
                <strong>Technical data</strong> — page views, approximate location derived from IP,
                browser type and error reports, used to keep the app fast and to detect abuse.
              </li>
              <li>
                <strong>Support messages</strong> — anything you send us through the support
                assistant or by email.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              3. Why we use it
            </h2>
            <p>
              To provide the service you paid for, to authenticate you, to send the alerts you opted
              into, to detect fraudulent referrals and abusive downloads, to fix crashes, and to
              understand which parts of the product are useful. We rely on performing our contract
              with you, our legitimate interest in security and improvement, and your consent for
              optional notifications.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              4. Notifications and email
            </h2>
            <p>
              Instant alerts, daily summaries, browser push and email notifications are all optional
              and controlled from your dashboard. You can mute them or turn them off entirely at any
              time. We also send essential service email such as password resets and purchase
              confirmations.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              5. Who processes data for us
            </h2>
            <p>
              We use a small number of providers acting on our instructions: cloud hosting and
              database infrastructure, our payment processor, our transactional email provider, and
              AI providers that generate blueprint text. They may process data outside your country;
              where that happens we rely on standard contractual protections.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              6. Cookies and local storage
            </h2>
            <p>
              We use storage on your device to keep you signed in, remember your theme and
              notification choices, cache the trend feed so it loads instantly, and attribute
              referral links. We do not run third-party advertising trackers.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              7. How long we keep it
            </h2>
            <p>
              Account and license records are kept while your account exists, and purchase records for
              as long as tax and accounting rules require. Technical logs and error reports are kept
              for a short retention window and then deleted or aggregated.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              8. Your rights
            </h2>
            <p>
              You can ask us for a copy of your data, correct it, export your blueprints, or have your
              account and personal data deleted. Contact us and we will act within 30 days. Deleting
              your account ends access to your license.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              9. Security
            </h2>
            <p>
              Data is stored on managed infrastructure with row-level access rules so each account can
              only read its own records. Shared blueprint links can be given an expiry, and downloads
              are rate-limited. No system is perfect — if a breach ever affects you, we will notify
              you.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              10. Children
            </h2>
            <p>The service is not intended for anyone under 16, and we do not knowingly collect their data.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              11. Contact
            </h2>
            <p>
              For any privacy request, reach us through the{" "}
              <Link to="/support" className="text-primary hover:underline">
                support page
              </Link>{" "}
              or email{" "}
              <a
                href="mailto:info@skaiboss.com"
                className="text-primary hover:underline"
              >
                info@skaiboss.com
              </a>
              . See also our{" "}
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>
              .
            </p>
            <SellerIdentity className="mt-6" />
          </section>

        </div>
      </div>
    </main>
  </>
);

export default Privacy;
